import { Flex } from '@utilitywarehouse/hearth-react';
import { Button, ValidationText } from '@utilitywarehouse/hearth-react';
import { FormikProps, getIn, useFormikContext } from 'formik';
import { ReactNode } from 'react';

import { Address } from '@generated/gql/graphql';

import { NestedFormValues } from '../types';
import { getFieldName } from '../utils';
import { AddressManualEntry } from './AddressManualEntry';
import { AddressSearch } from './AddressSearch';
import { useStore } from './store';

interface AddressFormProps<T> {
  name: T;
  children?: ReactNode;
  className?: string;
  isLoading: boolean;
  onChange?(addressSelection: Address): void;
}

const AddressForm = <T extends string>({
  name,
  children,
  className = '',
  isLoading,
  onChange,
}: AddressFormProps<T>) => {
  const { values, setFieldValue }: FormikProps<NestedFormValues<T>> =
    useFormikContext();

  const {
    [name]: { addresses, hasFetchedAddresses },
  } = useStore();

  if (!getIn(values, name, null)) {
    throw new Error('AddressForm fields state is missing in Formik context');
  }

  const isManualEntry = values?.[name]?.manualEntry;

  const handleToggleManualInput = () => {
    setFieldValue(getFieldName(name, 'manualEntry'), !isManualEntry);
  };

  return (
    <Flex direction="column" spacing="xl" className={className}>
      <Flex direction="column" spacing="sm">
        {!isManualEntry && (
          <>
            <AddressSearch
              isLoading={isLoading}
              formName={name}
              manualEntry={isManualEntry}
              onChange={onChange}
            />
            {hasFetchedAddresses && addresses.length === 0 && (
              <ValidationText status="invalid">
                No addresses found for this post&nbsp;code
              </ValidationText>
            )}
          </>
        )}

        {isManualEntry && (
          <AddressManualEntry
            formName={name}
            onChange={onChange}
            isLoading={isLoading}
          />
        )}

        <Flex>
          <Button
            variant="ghost"
            size="sm"
            paddingNone
            onClick={handleToggleManualInput}
            type="button"
            data-testid="addressPickerManualEntry"
          >
            {isManualEntry
              ? 'Search for a different address'
              : 'Enter address manually'}
          </Button>
        </Flex>
      </Flex>
      {children}
    </Flex>
  );
};

export { AddressForm };
